import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Col, Container, Row, Table } from 'reactstrap';
import Loading from '../../../shared/components/custom/Loading';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import { formatCurrencyNumber } from '../../../util/functions';
import CheckIcon from '../../../shared/components/custom/CheckIcon';

const LeaderboardPage = ({ planId }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const config = useConfig();

  const [mode, setMode] = useState('user');
  const filteredLeaderboard = useMemo(() => {
    return leaderboard.filter((row) => {
      const companyIdExists = !!row.companyId;
      return mode === 'company' ? companyIdExists : !companyIdExists;
    });
  }, [leaderboard, mode]);
  const p = partial('goalSponsor');
  const s = partial('shared');

  const fetchLeaderboard = async () => {
    setLoadingLeaderboard(true);
    try {
      const resLeaderboard =
        await Api.goalSponsor.leaderboard.getLeaderboardForPlan(planId);
      const companyIds = resLeaderboard
        .filter((row) => row.companyId)
        .map((row) => row.companyId);
      const userCloudIds = resLeaderboard
        .filter((row) => row.userId)
        .map((row) => row.userId);
      const [{ data: foundUsers }, companies] = await Promise.all([
        Api.users.getUsersByCloudIds(userCloudIds),
        companyIds.length > 0
          ? Api.companies.getCompaniesByIds(companyIds)
          : Promise.resolve([]),
      ]);
      const mappedLeaderboard = resLeaderboard.map((row) => {
        const foundCompany = companies.find(
          (company) => company.id === row.companyId
        );
        const customCompanyName = foundCompany
          ? foundCompany.clubRelations.find(
              (relation) => relation.clubId == config.ntb_team_id
            )?.name
          : null;
        const foundUser = foundUsers.find(
          (user) => user.userCloudId === row.userId
        );
        return {
          ...row,
          company: foundCompany ? { ...foundCompany, customCompanyName } : null,
          user: foundUser,
        };
      });
      setLeaderboard(mappedLeaderboard);
      setLoadingLeaderboard(false);
    } catch (err) {
      console.log(err);
      toast.error(err || `${p('leaderboardRetrievingERROR')}`);
      setLoadingLeaderboard(false);
    }
  };
  useEffect(() => {
    fetchLeaderboard();
  }, []);
  const getEntityUrl = (row) => {
    if (row.company) {
      return `/companies/edit/${row.company.id}?tab=1`;
    }
    if (row.user) {
      return `/users/edit/${row.userId}`;
    }
    // No company and no linked app user - a subscription taken out from the
    // club website rather than the app, so there's nothing to link to.
    return null;
  };

  const renderLeaderboard = () => {
    return filteredLeaderboard.map((row, i) => (
      <tr
        style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
        key={`leaderboard-${row.total}-${row.companyId}-${row.userId}`}
      >
        <td>
          <CheckIcon state={row.status === 'active'} />
        </td>
        <td>
          <div className="flex-Vcenter">
            {getEntityUrl(row) ? (
              <Link to={getEntityUrl(row)}>
                {row.company
                  ? row.company.customCompanyName
                  : `${row.user.firstname || ''} ${row.user.lastname || ''}`}
              </Link>
            ) : (
              <>
                <span>{row.userName || row.userEmail || '-'}</span>
                <Badge pill color="secondary" className="marginLeft-8">
                  {s('website')}
                </Badge>
              </>
            )}
          </div>
        </td>
        <td>
          {formatCurrencyNumber(Number(row.total) / 100)} {config.currencyCaps}
        </td>
      </tr>
    ));
  };
  return (
    <Container>
      <Loading loading={loadingLeaderboard} />
      <Row className="ml-1">
        <Button
          outline={mode !== 'user'}
          size="sm"
          color="primary"
          onClick={() => setMode('user')}
        >
          {p('users')}
        </Button>
        <Button
          outline={mode !== 'company'}
          size="sm"
          color="primary"
          onClick={() => setMode('company')}
        >
          {p('companies')}
        </Button>
      </Row>
      <Row>
        <Col>
          <div
            style={{
              display: 'flex',
              paddingBottom: 5,
              background: '#fff',
              borderRadius: 6,
              boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.08)',
            }}
          >
            <Table responsive striped>
              <thead>
                <tr>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('active')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{s('name')}</div>
                  </th>
                  <th style={{ color: '#4D576A' }}>
                    <div className="flex alignItems-center">{p('total')}</div>
                  </th>
                </tr>
              </thead>
              <tbody>{renderLeaderboard()}</tbody>
            </Table>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default LeaderboardPage;
